import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import { OutstandingAgreementType, PackageType } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import Agreement from 'common/bento/modules/Agreement';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SuspenseQuery from 'components/SuspenseQuery';
import NavigationContext from 'features/Onboarding/components/OBNavigation/NavigationContext';
import OnboardingPageAdapter from 'features/Onboarding/components/OnboardingPageAdapter';
import BusinessInfoComplete from 'features/Onboarding/legacy/Pages/BusinessInfoComplete';
import ShineStartBusinessInfo from 'features/Onboarding/legacy/Pages/ShineStartBusinessInfo';
import ShineStartBusinessInfoRefused from 'features/Onboarding/legacy/Pages/ShineStartBusinessInfoRefused';
import ShineStartNextSteps from 'features/Onboarding/legacy/Pages/ShineStartNextSteps';
import CompanyCompliance from 'features/Onboarding/modules/CompanyCompliance';
import PersonalInfo from 'features/Onboarding/modules/PersonalInfo/PersonalInfo';
import PricingPlan from 'features/Onboarding/modules/PricingPlan';
import Verification from 'features/Onboarding/modules/Verification';
import { ApplicantType, ApplicationType } from 'features/Onboarding/types';
import logger from 'helpers/logger';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import useOverrideSetCurrentPage from '../../hooks/useOverrideSetCurrentPage';
import { User } from '../../legacy/types';
import { ApplicationTypeContextType } from '../../lib/applicationType';
import { ShineStartDataDocument } from './graphql/queries/shineStartData.gql';
import machineConfig, { model, State } from './machine';
import getNavigationSteps from './navigation/getNavigationSteps';
import Eligibility from './pages/Eligibility/Eligibility';

const logImpossibleEvent = (event: string, state: string) => {
  logger.error(`Impossible event '${event}' in state ${state}`);
};

const OnboardingShineStartComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const adaptedPageProps = {
      onPrev,
      setCurrentPage: useOverrideSetCurrentPage(send),
      user: moduleData.data.viewer.profile as unknown as User,
    };

    const stringifiedState = stringifyState<State>(state);

    const getContent = () => {
      switch (stringifiedState) {
        case State.ELIGIBILITY:
          return (
            <Eligibility
              onNext={() => send('NEXT')}
              onPrev={() => send('PREV')}
            />
          );

        case State.BUSINESS_INFO_FORM: {
          return (
            <ShineStartBusinessInfo
              onFormCompleted={() => send('NEXT')}
              onFormRefused={() => send('BUSINESS_INFO_REFUSED')}
              onPrev={onPrev}
            />
          );
        }

        case State.BUSINESS_INFO_REFUSED: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={ShineStartBusinessInfoRefused}
              {...adaptedPageProps}
            />
          );
        }

        case State.BUSINESS_INFO_COMPLETE: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={BusinessInfoComplete}
              {...adaptedPageProps}
              setCurrentPage={(page) => {
                switch (page) {
                  case 'verification-guide': {
                    return send(model.events.NEXT());
                  }

                  default: {
                    logImpossibleEvent(page, stringifiedState);
                    return null;
                  }
                }
              }}
            />
          );
        }

        case State.USER_AGREEMENT: {
          return (
            <Agreement
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send(model.events.NEXT())
                  : onPrev()
              }
              type={OutstandingAgreementType.Cgu}
            />
          );
        }

        case State.PLAN_CHOICE: {
          return (
            <PricingPlan
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send(model.events.NEXT())
                  : onPrev()
              }
              packageType={PackageType.ShineStart}
              shouldShowPackagePriceWithVAT
            />
          );
        }

        case State.NEXT_STEPS: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={ShineStartNextSteps}
              {...adaptedPageProps}
            />
          );
        }

        case State.INIT:
        case State.DONE:
        case State.ABORT:
        case State.ABORT_APPLICATION:
        case State.SUBMIT_APPLICATION:
          return <OBLoaderPage onPrev={() => null} />;
      }

      const { company } = moduleData.data.viewer;
      if (!company) {
        throw new Error(`Company not found for state ${stringifiedState}`);
      }

      switch (stringifiedState) {
        case State.PERSONAL_INFO: {
          return (
            <PersonalInfo
              applicantType={ApplicantType.AccountHolder}
              applicationType={ApplicationType.ShineStart}
              companyProfileId={company.companyProfileId}
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send(model.events.NEXT())
                  : onPrev()
              }
            />
          );
        }

        case State.COMPANY_COMPLIANCE: {
          return (
            <CompanyCompliance
              companyProfileId={company.companyProfileId}
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send(model.events.NEXT())
                  : onPrev()
              }
            />
          );
        }

        case State.VERIFICATION: {
          return (
            <Verification
              applicantType={ApplicantType.AccountHolder}
              applicationType={ApplicationType.ShineStart}
              companyProfileId={company.companyProfileId}
              isMainApplicant
              isShineStart
              onDone={() => send(model.events.NEXT())}
            />
          );
        }

        default: {
          return assertNever(stringifiedState);
        }
      }
    };

    return (
      <NavigationContext.Provider
        value={{ steps: getNavigationSteps(stringifyState<State>(state)) }}
      >
        {getContent()}
      </NavigationContext.Provider>
    );
  },

  {
    devTools: true,
    machineConfig,
  },
);

interface OnboardingShineStartProps {
  applicationTypeContext: ApplicationTypeContextType;
}

const OnboardingShineStart = asBentoModule<OnboardingShineStartProps>(
  ({ applicationTypeContext, onDone }) => {
    const companyContext = useCompanyContext();
    const { companyProfileId } = companyContext;

    const query = useQuery(ShineStartDataDocument, {
      variables: {
        companyProfileId: companyProfileId ?? '',
        includeCompany: Boolean(companyProfileId),
      },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(data) => (
          <OnboardingShineStartComponent
            moduleData={{
              applicationTypeContext,
              companyContext,
              data,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

export default OnboardingShineStart;
